/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Filter } from '../../../model/Filter';
import { Command } from '../../../model/Command';
import { ListController } from '../../ListController';

class CauseListController extends ListController {
    /** @ngInject */
    constructor(domainService, originService, causeService, waitSpinnerService, $q, $state, $stateParams, $log,
                notificationService, configuration, filterService, dialogService, $scope) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.domainService = domainService;
        this.originService = originService;
        this.causeService = causeService;
        this.filterService = filterService;
        this.dialogService = dialogService;
        this.$scope = $scope;
        // mdDataTable doesn't support ES6 syntax with $ctrl variable in templates. That's why we must attach the reload
        // callback to the scope with an arrow function so as it can be called by this component with the correct 'this'
        // binding.
        this.$scope.reload = () => this.reload();
        this.$log.debug('Controller \'CauseList\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.paging.sort = 'attribute.category.origin.domain.name';
        this.commands = [];
        this.initFilters();

        this.handleRestServiceCall(this.loadFilters(), () => {
            this.initCommands();
            this.reload();
        }).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Initializes the filters available at the top of the view.
     */
    initFilters() {
        this.domainFilter = Filter.equalFilterEmpty('attribute.category.origin.domain.id', 'model.domain.domain', 'SELECT', [],
            domainId => this.getFilterOrigins(domainId));

        this.originFilter = Filter.equalFilterEmpty('attribute.category.origin.id','model.cause.origin', 'SELECT', []);

        this.filters.push(
            this.domainFilter,
            this.originFilter,
            Filter.emptyLikeFilter('attribute.category.name', 'model.cause.category'),
            Filter.emptyLikeFilter('name', 'model.cause.name')
        );
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        this.commands.push(
            Command.stateCommand('ui.cause.create', 'images/svg/add.svg', 'gmao.organization.cause.create'),
            Command.handlerCommand(undefined, 'images/svg/reset.svg', () => this.reload())
        );
    }

    /**
     * Loads data in filters.
     */
    loadFilters() {
        return this.getFilterDomains();
    }

    /**
     * Gets the list of domains.
     *
     * @return {Object[]} Promise providing an array of domains.
     */
    getFilterDomains() {
        return this.filterService.getFilterOptions(this.domainService, this.domainFilter, 'name');
    }

    /**
     * Gets the list of origins for a given domain.
     *
     * @param {Number} [domainId] Domain ID, may be undefined if no domain is selected.
     * @return {Object[]} Promise providing an array of origin.
     */
    getFilterOrigins(domainId) {
        return this.filterService.getFilterOptions(this.originService,
            this.originFilter, 'name', [Filter.equalFilter('domain.id', domainId)]);
    }

    /**
     * Reloads the list of entities.
     *
     * @return {Promise} A promise providing the list of entities.
     */
    reload() {
        return this.handleRestServiceCall(
            this.causeService.searchEntities({
                params: this.filterService.buildPagingParams(this.paging),
                data: this.filterService.buildFilters(this.filters)
            }),
            data => {
                this.model.data = data.content;
                this.model.totalElements = data.totalElements;
            }
        );
    }

    /**
     * Shows a confirmation dialog before deleting a cause. If the dialog is confirmed, the cause is deleted and the list
     * of domains is reloaded.
     *
     * @param {integer} id Cause ID.
     * @return {Promise} A promise containing the confirmation result.
     */
    showCauseDeleteConfirmationDialog(id) {
        return this.showDeleteConfirmationDialog('ui.cause.deleteconfirmation').then(confirmed => {
            if (confirmed) {
                this.deleteCause(id).then(() => {
                    this.reload();
                })
            }
            return confirmed;
        });
    }

    /**
     * Shows a confirmation dialog before deleting an entity. If the dialog is confirmed, the entity is deleted.
     *
     * @param {String} messageKey Message key displayed in the dialog (translated).
     * @return {Promise} A promise containing the confirmation result.
     */
    showDeleteConfirmationDialog(messageKey) {
        return this.dialogService.showConfirmationDialogWithMessage(messageKey);
    }

    /**
     * Deletes immediately a cause without confirmation.
     *
     * @param {integer} id Domain ID.
     * @return {Promise} A promise which resolves when the delete is done.
     */
    deleteCause(id) {
        return this.handleRestServiceCall(this.causeService.deleteEntity(id), () => {
            this.notificationService.pushMessage('ui.cause.deleted', this.configuration.NOTIFIER_LEVEL_INFO);
        });
    }
}

export const causeListComponent = {
    template: require('./cause-list.html'),
    controller: CauseListController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/organization/component/CauseList.js